import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/helpers';
import { createPost } from '../utils/postUtils';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Camera, Edit3, MapPin, Calendar, FileText, ImageIcon, Users, Heart,
  MessageCircle, Send, Loader2, Trash2, X, Swords, Trophy, UserPlus,
  UserCheck, UserMinus, Award, Star, TrendingUp, Clock, CheckCircle,
  XCircle, Bell, Activity, Lock, Globe, Eye, Briefcase, GraduationCap,
  BookOpen, Film, Hash, Save
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Badge icons mapping - now using image paths
const BADGE_ICONS = {
  indian_flag: "/badges/indian_flag.png",
  bjp: "/badges/bjp.png",
  inc: "/badges/inc.png",
  aap: "/badges/aap.png",
  tmc: "/badges/tmc.png",
  dmk: "/badges/dmk.png",
  sp: "/badges/sp.png",
  bsp: "/badges/bsp.png",
  ncp: "/badges/ncp.png",
  shiv_sena: "/badges/shiv_sena.png",
  jdu: "/badges/jdu.png",
  rjd: "/badges/rjd.png",
  ysrcp: "/badges/ysrcp.png",
  tdp: "/badges/tdp.png",
  brs: "/badges/brs.png",
  cpim: "/badges/cpim.png",
  polivox_citizen: "/badges/polivox_citizen.png",
  civic_activist: "/badges/civic_activist.png",
  tree: "/badges/tree.png",
  mountain: "/badges/mountain.png",
  river: "/badges/river.png"
};

// Default badge icon for fallback
const DEFAULT_BADGE = "/badges/polivox_citizen.png";

// Privacy options
const PRIVACY_OPTIONS = [
  { value: "public", label: "Public", icon: Globe },
  { value: "friends", label: "Friends Only", icon: Users },
  { value: "only_me", label: "Only Me", icon: Lock },
];

// Relationship status options
const RELATIONSHIP_OPTIONS = [
  "Single", "Married", "In a relationship", "Looking", "Need a partner", "Prefer not to say"
];

// Political view options
const POLITICAL_OPTIONS = [
  "Left", "Center-Left", "Center", "Center-Right", "Right", "None", "Prefer not to say"
];

const Profile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canParticipate, triggerOnboarding } = useAuth();
  const [profileUser, setProfileUser] = useState(null);
  const [socialPosts, setSocialPosts] = useState([]);
  const [issues, setIssues] = useState([]);
  const [debates, setDebates] = useState({ rooms: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  
  // Edit mode
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // New post
  const [newPostCaption, setNewPostCaption] = useState('');
  const [newPostImage, setNewPostImage] = useState(null);
  const [newPostPreview, setNewPostPreview] = useState(null);
  const [posting, setPosting] = useState(false);
  
  // Photo upload refs
  const profilePhotoRef = useRef(null);
  const coverPhotoRef = useRef(null);
  
  // Cache busting for profile photo
  const [photoTimestamp, setPhotoTimestamp] = useState(Date.now());
  
  // Cover photo adjustment
  const [showCoverAdjust, setShowCoverAdjust] = useState(false);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverPositionY, setCoverPositionY] = useState(50);
  const [tempCoverFile, setTempCoverFile] = useState(null);
  const [isDraggingCover, setIsDraggingCover] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [savingCover, setSavingCover] = useState(false);
  
  // Likes state
  const [likedPosts, setLikedPosts] = useState({});
  
  // Social Layer State
  const [citizenScore, setCitizenScore] = useState(null);
  const [badges, setBadges] = useState([]);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [friendStatus, setFriendStatus] = useState(null);
  const [friendRequests, setFriendRequests] = useState({ received: [], sent: [] });
  const [showFriendRequests, setShowFriendRequests] = useState(false);
  const [friends, setFriends] = useState([]);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  useEffect(() => {
    setIsOwnProfile(user && user.user_id === id);
    fetchProfileData();
    fetchFriends(); // Always fetch friends for any profile
    if (user) {
      fetchCitizenScore();
      fetchBadges();
      if (user.user_id !== id) {
        checkFriendStatus();
        checkFollowStatus();
      } else {
        fetchFriendRequests();
      }
    }
    fetchFollowCounts();
  }, [id, user]);

  const fetchProfileData = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const profileRes = await axios.get(`${API_URL}/api/profile/${id}`, { withCredentials: true });
      if (profileRes.data) {
        setProfileUser(profileRes.data);
        setSelectedBadge(profileRes.data.badge_type);
        setCoverPositionY(profileRes.data.cover_position_y ?? 50);
        
        // Initialize edit form with profile data
        setEditForm({
          bio: profileRes.data.bio || '',
          education: profileRes.data.education || '',
          profession: profileRes.data.profession || '',
          relationship_status: profileRes.data.relationship_status || '',
          political_view: profileRes.data.political_view || '',
          interests: profileRes.data.interests || '',
          favorite_books: profileRes.data.favorite_books || '',
          favorite_movies: profileRes.data.favorite_movies || '',
          location: profileRes.data.location || '',
          bio_privacy: profileRes.data.bio_privacy || 'public',
          education_privacy: profileRes.data.education_privacy || 'public',
          profession_privacy: profileRes.data.profession_privacy || 'public',
          relationship_status_privacy: profileRes.data.relationship_status_privacy || 'friends',
          political_view_privacy: profileRes.data.political_view_privacy || 'public',
          interests_privacy: profileRes.data.interests_privacy || 'public',
          favorite_books_privacy: profileRes.data.favorite_books_privacy || 'public',
          favorite_movies_privacy: profileRes.data.favorite_movies_privacy || 'public',
          location_privacy: profileRes.data.location_privacy || 'public',
        });
      }

      const postsRes = await axios.get(`${API_URL}/api/social-posts?user_id=${id}`);
      setSocialPosts(postsRes.data?.posts || []);

      const issuesRes = await axios.get(`${API_URL}/api/users/${id}/issues`);
      setIssues(issuesRes.data || []);

      try {
        const debatesRes = await axios.get(`${API_URL}/api/users/${id}/debates`);
        setDebates(debatesRes.data || { rooms: [], stats: {} });
      } catch (e) {
        console.log('No debates data');
      }
      
      if (user) {
        const likeStatus = {};
        const posts = postsRes.data?.posts || [];
        for (const post of posts) {
          try {
            const likeRes = await axios.get(`${API_URL}/api/social-posts/${post.post_id}/likes`, {
              withCredentials: true
            });
            likeStatus[post.post_id] = likeRes.data?.liked || false;
          } catch (e) {
            likeStatus[post.post_id] = false;
          }
        }
        setLikedPosts(likeStatus);
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
      if (showLoader) {
        toast.error('Failed to load profile');
      }
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const fetchCitizenScore = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/citizen-score/${id}`);
      setCitizenScore(res.data);
    } catch (e) {
      console.log('No citizen score');
    }
  };

  const fetchBadges = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/badges`);
      setBadges(res.data.badges || {});
    } catch (e) {
      console.log('Failed to fetch badges');
    }
  };

  const checkFriendStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/friends/requests`, { withCredentials: true });
      const received = res.data.received || [];
      const sent = res.data.sent || [];
      
      const receivedFromProfile = received.find(r => r.sender_user_id === id);
      if (receivedFromProfile) {
        setFriendStatus('request_received');
        return;
      }
      
      const sentToProfile = sent.find(r => r.receiver_user_id === id);
      if (sentToProfile) {
        setFriendStatus('pending');
        return;
      }
      
      const friendsRes = await axios.get(`${API_URL}/api/friends/${user.user_id}`, { withCredentials: true });
      const friendsList = friendsRes.data.friends || [];
      const isFriend = friendsList.some(f => f.user_id === id);
      setFriendStatus(isFriend ? 'friends' : 'none');
    } catch (e) {
      setFriendStatus('none');
    }
  };

  const checkFollowStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/following/${user.user_id}`);
      const following = res.data.following || [];
      setIsFollowing(following.some(f => f.user_id === id));
    } catch (e) {
      setIsFollowing(false);
    }
  };

  const fetchFollowCounts = async () => {
    try {
      const [followersRes, followingRes] = await Promise.all([
        axios.get(`${API_URL}/api/followers/${id}`),
        axios.get(`${API_URL}/api/following/${id}`)
      ]);
      setFollowersCount(followersRes.data.followers?.length || 0);
      setFollowingCount(followingRes.data.following?.length || 0);
    } catch (e) {
      console.log('Failed to fetch follow counts');
    }
  };

  const fetchFriendRequests = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/friends/requests`, { withCredentials: true });
      console.log('[FriendRequests] Fetched:', res.data);
      setFriendRequests(res.data);
    } catch (e) {
      console.error('[FriendRequests] Error:', e.response?.data || e.message);
      setFriendRequests({ received: [], sent: [] });
    }
  };

  const fetchFriends = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/friends/${id}`, { withCredentials: true });
      setFriends(res.data.friends || []);
    } catch (e) {
      console.log('Failed to fetch friends');
    }
  };

  const fetchActivities = async () => {
    setLoadingActivities(true);
    try {
      const res = await axios.get(`${API_URL}/api/activities/${id}`);
      setActivities(res.data.activities || []);
    } catch (e) {
      console.log('Failed to fetch activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('photo', file);
    
    try {
      await axios.post(`${API_URL}/api/profile/photo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile photo updated');
      setPhotoTimestamp(Date.now()); // Cache bust
      fetchProfileData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    }
  };

  const handleCoverPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Show preview mode instead of instant save
    setTempCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
    setCoverPositionY(50); // Reset position for new image
    setShowCoverAdjust(true);
  };

  const handleCoverDragStart = (e) => {
    e.preventDefault();
    setIsDraggingCover(true);
    setDragStartY(e.clientY || e.touches?.[0]?.clientY);
  };

  const handleCoverDrag = (e) => {
    if (!isDraggingCover) return;
    e.preventDefault();
    const currentY = e.clientY || e.touches?.[0]?.clientY;
    if (!currentY) return;
    
    const rawDelta = currentY - dragStartY;
    // Add resistance at edges for better UX feel
    const resistance = 0.25;
    const delta = rawDelta * resistance;
    
    setCoverPositionY(prev => {
      const newPos = prev + delta;
      // Soft boundaries with easing
      if (newPos < 0) return prev * 0.9;
      if (newPos > 100) return 100 - (100 - prev) * 0.9;
      return Math.max(0, Math.min(100, newPos));
    });
    setDragStartY(currentY);
  };

  const handleCoverDragEnd = () => {
    setIsDraggingCover(false);
  };

  const handleSaveCover = async () => {
    if (!tempCoverFile) return;
    
    setSavingCover(true);
    try {
      const formData = new FormData();
      formData.append('photo', tempCoverFile);
      
      // Upload cover photo
      await axios.post(`${API_URL}/api/profile/cover`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Save the position separately
      await axios.put(`${API_URL}/api/profile`, { cover_position_y: Math.round(coverPositionY) }, { withCredentials: true });
      
      toast.success('Cover photo updated!');
      
      // Update local state immediately without full reload
      setProfileUser(prev => ({
        ...prev,
        cover_photo: coverPreview,
        cover_position_y: Math.round(coverPositionY)
      }));
      
      setShowCoverAdjust(false);
      setCoverPreview(null);
      setTempCoverFile(null);
      
      // Background refresh to get the actual URL from server
      setTimeout(() => {
        fetchProfileData(false);
      }, 500);
    } catch (error) {
      console.error('Cover save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload cover photo');
    } finally {
      setSavingCover(false);
    }
  };

  const handleCancelCover = () => {
    setShowCoverAdjust(false);
    setCoverPreview(null);
    setTempCoverFile(null);
    setCoverPositionY(profileUser?.cover_position_y || 50);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      console.log('[Profile] Saving:', editForm);
      const res = await axios.put(`${API_URL}/api/profile`, editForm, { withCredentials: true });
      console.log('[Profile] Response:', res.data);
      toast.success('Profile updated!');
      setShowEditModal(false);
      fetchProfileData();
    } catch (error) {
      console.error('[Profile] Error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateBadge = async (badgeType) => {
    try {
      console.log('[Badge] Updating to:', badgeType);
      const res = await axios.put(`${API_URL}/api/profile`, { badge_type: badgeType }, { withCredentials: true });
      console.log('[Badge] Update response:', res.data);
      setSelectedBadge(badgeType);
      setProfileUser(prev => ({ ...prev, badge_type: badgeType }));
      toast.success(badgeType ? 'Badge updated!' : 'Badge removed');
      setShowBadgeModal(false);
      fetchProfileData();
    } catch (error) {
      console.error('[Badge] Error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to update badge');
    }
  };

  const handleSendFriendRequest = async () => {
    try {
      console.log('[FriendRequest] Sending to:', id);
      await axios.post(`${API_URL}/api/friends/request`, 
        { receiver_user_id: id }, 
        { withCredentials: true }
      );
      setFriendStatus('pending');
      toast.success('Friend request sent!');
    } catch (error) {
      console.error('[FriendRequest] Error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to send friend request');
    }
  };

  const handleRespondToFriendRequest = async (requestId, action) => {
    try {
      await axios.post(`${API_URL}/api/friends/requests/${requestId}`, 
        { action }, 
        { withCredentials: true }
      );
      toast.success(action === 'accept' ? 'Friend request accepted!' : 'Friend request declined');
      await fetchFriendRequests();
      if (action === 'accept') {
        await fetchFriends();
        // Also refresh profile to get updated friends_count
        await fetchProfileData(false);
        setFriendStatus('friends');
      } else {
        setFriendStatus('none');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond to request');
    }
  };

  const handleRemoveFriend = async () => {
    if (!window.confirm('Remove this friend?')) return;
    try {
      await axios.delete(`${API_URL}/api/friends/${id}`, { withCredentials: true });
      setFriendStatus('none');
      toast.success('Friend removed');
      fetchFriends();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove friend');
    }
  };

  const handleFollow = async () => {
    try {
      await axios.post(`${API_URL}/api/follow/${id}`, {}, { withCredentials: true });
      setIsFollowing(true);
      setFollowersCount(prev => prev + 1);
      toast.success('Following');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to follow');
    }
  };

  const handleUnfollow = async () => {
    try {
      await axios.delete(`${API_URL}/api/follow/${id}`, { withCredentials: true });
      setIsFollowing(false);
      setFollowersCount(prev => Math.max(0, prev - 1));
      toast.success('Unfollowed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unfollow');
    }
  };

  const handleCreatePost = async () => {
    // Check profile completion before posting
    if (!canParticipate()) {
      triggerOnboarding();
      return;
    }
    
    setPosting(true);
    try {
      // Use shared createPost function
      const response = await createPost({
        caption: newPostCaption,
        media: newPostImage
      });
      
      console.log('[Profile] Posting response:', response);
      
      toast.success('Post created!');
      setNewPostCaption('');
      setNewPostImage(null);
      setNewPostPreview(null);
      fetchProfileData();
    } catch (error) {
      console.error('[Profile] Post creation error:', error);
      // Handle 403 from backend - check for profile completion message
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || '';
        if (detail.toLowerCase().includes('complete your responsible citizen profile')) {
          triggerOnboarding();
          return;
        }
        // Other 403 errors (e.g., banned user)
        toast.error(detail || 'You are not authorized to perform this action');
        return;
      }
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create post';
      toast.error(errorMessage);
    } finally {
      setPosting(false);
    }
  };

  const handleLikePost = async (postId) => {
    if (!user) {
      toast.error('Please login to like posts');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/social-posts/${postId}/like`, {}, {
        withCredentials: true
      });
      setLikedPosts(prev => ({ ...prev, [postId]: response.data.liked }));
      setSocialPosts(prev => prev.map(post => 
        post.post_id === postId 
          ? { ...post, likes_count: response.data.likes_count }
          : post
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to like post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/social-posts/${postId}`, { withCredentials: true });
      toast.success('Post deleted');
      setSocialPosts(prev => prev.filter(p => p.post_id !== postId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${API_URL}${url}`;
  };

  const getRankColor = (rank) => {
    const colors = {
      'Civic Champion': 'from-yellow-400 to-amber-600',
      'Community Leader': 'from-purple-400 to-purple-600',
      'Active Citizen': 'from-blue-400 to-blue-600',
      'Engaged Citizen': 'from-green-400 to-green-600',
      'New Citizen': 'from-gray-400 to-gray-600'
    };
    return colors[rank] || 'from-gray-400 to-gray-600';
  };

  const getActivityIcon = (type) => {
    const icons = {
      'issue_reported': FileText,
      'issue_supported': Heart,
      'leader_rated': Star,
      'social_post_created': Edit3,
      'friend_added': Users,
      'comment_posted': MessageCircle
    };
    return icons[type] || Activity;
  };

  const getActivityDescription = (activity) => {
    const descriptions = {
      'issue_reported': 'Reported a new issue',
      'issue_supported': 'Supported an issue',
      'leader_rated': 'Rated a leader',
      'social_post_created': 'Created a new post',
      'friend_added': 'Made a new friend',
      'comment_posted': 'Posted a comment'
    };
    return activity.description || descriptions[activity.activity_type] || activity.activity_type;
  };

  const getPrivacyIcon = (privacy) => {
    const icons = { public: Globe, friends: Users, only_me: Lock };
    return icons[privacy] || Globe;
  };

  const PrivacySelect = ({ value, onChange, label }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-28 h-8 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PRIVACY_OPTIONS.map(opt => (
          <SelectItem key={opt.value} value={opt.value}>
            <div className="flex items-center gap-2">
              <opt.icon className="h-3 w-3" />
              {opt.label}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const ProfileField = ({ icon: Icon, label, value, privacy }) => {
    if (!value) return null;
    const PrivacyIcon = getPrivacyIcon(privacy);
    return (
      <div className="flex items-start gap-3 py-2">
        <Icon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-gray-800">{value}</p>
        </div>
        {isOwnProfile && (
          <PrivacyIcon className="h-4 w-4 text-gray-300" title={`Visible to: ${privacy}`} />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">User not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Cover Photo with Adjustment Mode */}
      <div 
        className="relative h-48 sm:h-64 md:h-72 bg-gradient-to-r from-blue-500 to-purple-600 overflow-hidden"
      >
        {/* Cover Image with smooth transition */}
        {(coverPreview || profileUser.cover_photo) && (
          <img 
            src={coverPreview || getImageUrl(profileUser.cover_photo)} 
            alt="Cover" 
            className={`w-full h-full object-cover select-none transition-all duration-150 ease-out ${
              showCoverAdjust 
                ? isDraggingCover ? 'cursor-grabbing' : 'cursor-grab'
                : ''
            }`}
            style={{ objectPosition: `center ${coverPositionY}%` }}
            draggable={false}
          />
        )}
        
        {/* Strong gradient overlay for text visibility */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.3) 60%, transparent 100%)'
          }}
        />
        
        {/* Cover adjustment controls - this div handles all drag events */}
        {showCoverAdjust && (
          <div 
            className={`absolute inset-0 flex flex-col items-center justify-center bg-black/40 select-none transition-colors ${
              isDraggingCover ? 'cursor-grabbing' : 'cursor-grab'
            }`}
            onMouseDown={handleCoverDragStart}
            onMouseMove={handleCoverDrag}
            onMouseUp={handleCoverDragEnd}
            onMouseLeave={handleCoverDragEnd}
            onTouchStart={handleCoverDragStart}
            onTouchMove={handleCoverDrag}
            onTouchEnd={handleCoverDragEnd}
          >
            <div className="bg-black/50 backdrop-blur-sm px-4 py-2 rounded-lg mb-4 pointer-events-none">
              <p className="text-white text-sm font-medium">
                Drag up/down to reposition
              </p>
            </div>
            <div className="flex gap-2 pointer-events-auto">
              <button
                onClick={handleSaveCover}
                disabled={savingCover}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 shadow-lg"
              >
                {savingCover ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </button>
              <button
                onClick={handleCancelCover}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-5 py-2.5 rounded-lg flex items-center gap-2 transition-colors shadow-lg"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </div>
          </div>
        )}
        
        {/* Edit cover button (only when not adjusting) */}
        {isOwnProfile && !showCoverAdjust && (
          <>
            <button
              onClick={() => coverPhotoRef.current?.click()}
              className="absolute bottom-4 right-4 bg-black/50 hover:bg-black/70 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors z-10"
            >
              <Camera className="h-4 w-4" />
              <span className="hidden sm:inline">Edit Cover</span>
            </button>
            <input ref={coverPhotoRef} type="file" accept="image/*" onChange={handleCoverPhotoUpload} className="hidden" />
          </>
        )}
      </div>

      {/* Profile Header - Consistent for all users */}
      <div className="max-w-5xl mx-auto px-4">
        <div className="relative -mt-16 sm:-mt-20 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            {/* Profile Photo */}
            <div className="relative flex-shrink-0">
              <div 
                className={`w-32 h-32 sm:w-36 sm:h-36 rounded-full border-4 border-white bg-white overflow-hidden shadow-lg ${isOwnProfile ? 'cursor-pointer group' : ''}`}
                onClick={() => isOwnProfile && profilePhotoRef.current?.click()}
              >
                {profileUser.picture ? (
                  <img src={`${getImageUrl(profileUser.picture)}?t=${photoTimestamp}`} alt={profileUser.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-4xl font-bold">
                    {profileUser.name?.charAt(0) || 'U'}
                  </div>
                )}
                {/* Hover overlay for clickable feedback */}
                {isOwnProfile && (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <Camera className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                  </div>
                )}
              </div>
              {/* Badge Display */}
              {profileUser.badge_type && BADGE_ICONS[profileUser.badge_type] && (
                <div 
                  className="absolute -bottom-1 -right-1 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:scale-110 transition-transform overflow-hidden"
                  onClick={() => isOwnProfile && setShowBadgeModal(true)}
                  title={badges[profileUser.badge_type]?.name || profileUser.badge_type}
                >
                  <img 
                    src={BADGE_ICONS[profileUser.badge_type]} 
                    alt={badges[profileUser.badge_type]?.name || profileUser.badge_type}
                    className="w-8 h-8 object-contain"
                    onError={(e) => { e.target.src = DEFAULT_BADGE; }}
                  />
                </div>
              )}
              {/* Camera icon - centered at bottom */}
              {isOwnProfile && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); profilePhotoRef.current?.click(); }}
                    className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-black/60 hover:bg-black/80 p-2 rounded-full shadow-md transition-colors"
                    title="Change profile photo"
                  >
                    <Camera className="h-4 w-4 text-white" />
                  </button>
                  <input ref={profilePhotoRef} type="file" accept="image/*" onChange={handleProfilePhotoUpload} className="hidden" />
                </>
              )}
            </div>

            {/* Name and Info - Takes remaining space */}
            <div className="flex-1 pb-4 min-w-0">
              {/* Name row with admin badge on same line */}
              <div className="flex items-center gap-2 flex-wrap">
                <h1 
                  className={`text-2xl sm:text-3xl font-bold whitespace-nowrap text-white`}
                  style={{ 
                    textShadow: '0 3px 8px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.8)'
                  }}
                >
                  {profileUser.name}
                </h1>
                {profileUser.is_verified && (
                  <CheckCircle className="h-5 w-5 text-blue-400 fill-blue-400 flex-shrink-0 drop-shadow-lg" />
                )}
                {profileUser.is_admin && (
                  <span 
                    className="px-2 py-0.5 text-[11px] font-semibold bg-purple-500/80 text-white rounded-md flex-shrink-0 backdrop-blur-sm"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                  >
                    Admin
                  </span>
                )}
              </div>
              {/* Username with premium white styling and blur background */}
              {profileUser.username && (
                <span 
                  className="inline-block mt-1 px-2.5 py-1 rounded-lg text-white font-medium"
                  style={{
                    background: 'rgba(0,0,0,0.4)',
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)',
                    textShadow: '0 3px 8px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.8)'
                  }}
                >
                  @{profileUser.username}
                </span>
              )}
              
              {/* Citizen Score Badge */}
              {citizenScore && (
                <div 
                  className={`inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-full bg-gradient-to-r ${getRankColor(citizenScore.citizen_rank)} text-white text-sm font-medium shadow-lg`}
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  <Award className="h-4 w-4" />
                  <span>{citizenScore.citizen_rank}</span>
                  <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{citizenScore.citizen_score} pts</span>
                </div>
              )}
              
              {/* Location */}
              {(profileUser.location || profileUser.state || profileUser.constituency) && (
                <div 
                  className="flex items-center gap-1 text-white/90 mt-2"
                  style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                >
                  <MapPin className="h-4 w-4 drop-shadow-lg" />
                  <span>{profileUser.location || [profileUser.constituency, profileUser.district, profileUser.state].filter(Boolean).join(', ')}</span>
                </div>
              )}
              
              {/* Stats Row */}
              <div 
                className="flex items-center gap-4 mt-2 text-sm text-gray-700"
              >
                <button onClick={() => setShowFriendsList(true)} className="hover:text-gray-900 transition-colors">
                  <span className="font-semibold text-gray-900">{friends.length || profileUser?.friends_count || 0}</span> friends
                </button>
                <span><span className="font-semibold text-gray-900">{followersCount || 0}</span> followers</span>
                <span><span className="font-semibold text-gray-900">{followingCount || 0}</span> following</span>
              </div>
              
              <div 
                className="flex items-center gap-1 text-gray-600 text-sm mt-1"
              >
                <Calendar className="h-4 w-4" />
                <span>Joined {formatDate(profileUser.created_at)}</span>
              </div>
              
              {/* Bio and Key Info - Compact display */}
              {profileUser.bio && (
                <p className="text-gray-700 text-sm mt-1.5 line-clamp-2">{profileUser.bio}</p>
              )}
              {profileUser.relationship_status && profileUser.relationship_status_privacy !== 'private' && (
                <p className="text-gray-500 text-xs mt-1 flex items-center gap-1">
                  <Heart className="h-3 w-3 text-pink-500" />
                  {profileUser.relationship_status === 'single' && 'Single'}
                  {profileUser.relationship_status === 'in_relationship' && 'In a relationship'}
                  {profileUser.relationship_status === 'married' && 'Married'}
                  {profileUser.relationship_status === 'complicated' && "It's complicated"}
                  {profileUser.relationship_status === 'looking' && 'Looking for partner'}
                  {!['single', 'in_relationship', 'married', 'complicated', 'looking'].includes(profileUser.relationship_status) && profileUser.relationship_status}
                </p>
              )}
            </div>

            {/* Action Buttons - Consistent layout */}
            <div className="flex flex-wrap gap-2 self-start sm:self-end flex-shrink-0">
              {isOwnProfile ? (
                <>
                  <Button onClick={() => setShowBadgeModal(true)} variant="outline" size="sm">
                    <Award className="h-4 w-4 mr-2" />Badge
                  </Button>
                  <Button onClick={() => { fetchFriendRequests(); setShowFriendRequests(true); }} variant="outline" size="sm" className="relative">
                    <Bell className="h-4 w-4 mr-2" />Requests
                    {friendRequests.received?.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {friendRequests.received.length}
                      </span>
                    )}
                  </Button>
                  <Button onClick={() => setShowEditModal(true)} size="sm">
                    <Edit3 className="h-4 w-4 mr-2" />Edit Profile
                  </Button>
                </>
              ) : (
                <>
                  {friendStatus === 'none' && (
                    <Button onClick={handleSendFriendRequest} size="sm"><UserPlus className="h-4 w-4 mr-2" />Add Friend</Button>
                  )}
                  {friendStatus === 'pending' && (
                    <Button variant="outline" size="sm" disabled><Clock className="h-4 w-4 mr-2" />Request Sent</Button>
                  )}
                  {friendStatus === 'request_received' && (
                    <Button onClick={() => { fetchFriendRequests(); setShowFriendRequests(true); }} size="sm" className="bg-green-600 hover:bg-green-700">
                      <UserCheck className="h-4 w-4 mr-2" />Respond
                    </Button>
                  )}
                  {friendStatus === 'friends' && (
                    <Button onClick={handleRemoveFriend} variant="outline" size="sm"><UserMinus className="h-4 w-4 mr-2" />Friends</Button>
                  )}
                  {profileUser.allow_followers !== false && (
                    isFollowing ? (
                      <Button onClick={handleUnfollow} variant="outline" size="sm"><UserCheck className="h-4 w-4 mr-2" />Following</Button>
                    ) : (
                      <Button onClick={handleFollow} variant="secondary" size="sm"><UserPlus className="h-4 w-4 mr-2" />Follow</Button>
                    )
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="posts" className="space-y-4">
          <TabsList className="bg-white shadow-sm rounded-lg p-1 flex-wrap">
            <TabsTrigger value="about" className="flex items-center gap-2" data-testid="tab-about">
              <Users className="h-4 w-4" />About
            </TabsTrigger>
            <TabsTrigger value="posts" className="flex items-center gap-2" data-testid="tab-posts">
              <FileText className="h-4 w-4" />Posts
            </TabsTrigger>
            <TabsTrigger value="issues" className="flex items-center gap-2" data-testid="tab-issues">
              <FileText className="h-4 w-4" />Issues ({issues.length})
            </TabsTrigger>
            <TabsTrigger value="debates" className="flex items-center gap-2" data-testid="tab-debates">
              <Swords className="h-4 w-4" />Debates ({debates.stats?.total_debates || 0})
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-2" onClick={fetchActivities} data-testid="tab-activity">
              <Activity className="h-4 w-4" />Activity
            </TabsTrigger>
          </TabsList>

          {/* About Tab - Enhanced with privacy-aware fields */}
          <TabsContent value="about">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">About</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-gray-500 uppercase mb-3">Basic Info</h4>
                  <ProfileField icon={FileText} label="Bio" value={profileUser.bio} privacy={profileUser.bio_privacy} />
                  <ProfileField icon={GraduationCap} label="Education" value={profileUser.education} privacy={profileUser.education_privacy} />
                  <ProfileField icon={Briefcase} label="Profession" value={profileUser.profession} privacy={profileUser.profession_privacy} />
                  <ProfileField icon={MapPin} label="Location" value={profileUser.location} privacy={profileUser.location_privacy} />
                </div>
                
                {/* Personal */}
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-gray-500 uppercase mb-3">Personal</h4>
                  <ProfileField icon={Heart} label="Relationship Status" value={profileUser.relationship_status} privacy={profileUser.relationship_status_privacy} />
                  <ProfileField icon={Star} label="Political View" value={profileUser.political_view} privacy={profileUser.political_view_privacy} />
                  <ProfileField icon={Hash} label="Interests" value={profileUser.interests} privacy={profileUser.interests_privacy} />
                </div>
                
                {/* Interests */}
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-gray-500 uppercase mb-3">Favorites</h4>
                  <ProfileField icon={BookOpen} label="Favorite Books" value={profileUser.favorite_books} privacy={profileUser.favorite_books_privacy} />
                  <ProfileField icon={Film} label="Favorite Movies" value={profileUser.favorite_movies} privacy={profileUser.favorite_movies_privacy} />
                </div>
                
                {/* Citizen Score Details */}
                {citizenScore && (
                  <div className="space-y-1">
                    <h4 className="text-sm font-medium text-gray-500 uppercase mb-3">Citizen Impact</h4>
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r ${getRankColor(citizenScore.citizen_rank)} text-white`}>
                      <Award className="h-5 w-5" />
                      <span className="font-semibold">{citizenScore.citizen_rank}</span>
                      <span className="bg-white/20 px-2 py-1 rounded-full text-sm">{citizenScore.citizen_score} points</span>
                    </div>
                    {citizenScore.next_rank && citizenScore.next_rank.points_needed > 0 && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <TrendingUp className="h-4 w-4" />
                          <span>{citizenScore.next_rank.points_needed} points to {citizenScore.next_rank.name}</span>
                        </div>
                        <div className="mt-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full bg-gradient-to-r ${getRankColor(citizenScore.next_rank.name)} transition-all`}
                            style={{ width: `${Math.min(100, (citizenScore.citizen_score / (citizenScore.citizen_score + citizenScore.next_rank.points_needed)) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 pt-6 mt-6 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{profileUser.issues_count || 0}</p>
                  <p className="text-sm text-gray-500">Issues</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{profileUser.posts_count || 0}</p>
                  <p className="text-sm text-gray-500">Posts</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{debates.stats?.total_debates || 0}</p>
                  <p className="text-sm text-gray-500">Debates</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Posts Tab */}
          <TabsContent value="posts" className="space-y-4">
            {isOwnProfile && (
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex gap-3">
                  <img src={getImageUrl(user.picture) || '/default-avatar.png'} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                  <div className="flex-1">
                    <Textarea
                      value={newPostCaption}
                      onChange={(e) => setNewPostCaption(e.target.value)}
                      placeholder="What's on your mind?"
                      rows={3}
                      maxLength={500}
                      className="resize-none"
                      data-testid="post-caption-input"
                    />
                    <p className={`text-xs mt-1 ${newPostCaption.trim().length < 10 ? 'text-red-500' : 'text-green-500'}`}>
                      {newPostCaption.trim().length < 10 ? (
                        <>Write at least 10 characters to post ({newPostCaption.trim().length}/10)</>
                      ) : (
                        <>✓ Ready to post</>
                      )}
                      <span className="float-right text-gray-400">{newPostCaption.length}/500</span>
                    </p>
                    
                    {newPostPreview && (
                      <div className="relative mt-3">
                        <img src={newPostPreview} alt="Preview" className="max-h-64 rounded-lg" />
                        <button onClick={() => { setNewPostImage(null); setNewPostPreview(null); }} className="absolute top-2 right-2 bg-black/50 text-white p-1 rounded-full">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <label className="flex items-center gap-2 text-gray-600 cursor-pointer hover:text-blue-600 transition-colors">
                        <ImageIcon className="h-5 w-5" />
                        <span className="text-sm">Photo</span>
                        <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; if (f) { setNewPostImage(f); setNewPostPreview(URL.createObjectURL(f)); }}} className="hidden" />
                      </label>
                      <Button onClick={handleCreatePost} disabled={posting || newPostCaption.length < 10} size="sm" data-testid="post-submit-btn">
                        {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" />Post</>}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {socialPosts.length > 0 ? (
              socialPosts.map((post) => (
                <div key={post.post_id} className="bg-white rounded-lg shadow-sm overflow-hidden" data-testid={`post-${post.post_id}`}>
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img src={getImageUrl(post.user_picture) || '/default-avatar.png'} alt={post.user_name} className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <span className={`text-sm font-medium ${post.is_verified ? 'text-blue-800' : 'text-gray-900'}`}>{post.user_name}</span>
                        <p className="text-xs text-gray-500">{formatDate(post.created_at)}</p>
                      </div>
                    </div>
                    {(isOwnProfile || user?.is_admin) && (
                      <button onClick={() => handleDeletePost(post.post_id)} className="text-gray-400 hover:text-red-500 p-1" data-testid={`delete-post-${post.post_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {post.caption && <div className="px-4 pb-2"><p className="text-gray-800 text-sm whitespace-pre-wrap">{post.caption}</p></div>}
                  {/* Handle all possible image fields: media_url, image_url, image - SAME AS HOMEPAGE */}
                  {(post.media_url || post.image_url || post.image) && (
                    <img 
                      src={getImageUrl(post.media_url || post.image_url || post.image)} 
                      alt="Post media"
                      loading="lazy"
                      className="w-full rounded-xl cursor-pointer mt-2 mb-2 mx-auto"
                      style={{ 
                        maxHeight: "350px", 
                        objectFit: "contain",
                        backgroundColor: "#f3f4f6"
                      }}
                      onClick={() => navigate(`/posts/${post.post_id}`)}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="px-4 py-2 border-t flex items-center gap-4 text-xs text-gray-500">
                    <span>{post.likes_count || 0} likes</span>
                    <span>{post.comments_count || 0} comments</span>
                  </div>
                  <div className="px-4 py-2 flex items-center gap-2 border-t">
                    <button onClick={() => handleLikePost(post.post_id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${likedPosts[post.post_id] ? 'text-red-500 bg-red-50' : 'text-gray-600 hover:bg-gray-100'}`} data-testid={`like-post-${post.post_id}`}>
                      <Heart className={`h-4 w-4 ${likedPosts[post.post_id] ? 'fill-current' : ''}`} />
                      Like
                    </button>
                    <button onClick={() => navigate(`/posts/${post.post_id}`)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors" data-testid={`comment-post-${post.post_id}`}>
                      <MessageCircle className="h-4 w-4" />
                      Comment
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No posts yet</p>
              </div>
            )}
          </TabsContent>

          {/* Issues Tab */}
          <TabsContent value="issues" className="space-y-4">
            {issues.length > 0 ? (
              issues.map((issue) => (
                <div key={issue.issue_id} onClick={() => navigate(`/issues/${issue.issue_id}`)} className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-testid={`issue-${issue.issue_id}`}>
                  <h3 className="font-semibold text-gray-900">{issue.title}</h3>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{issue.description}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                    <span className="px-2 py-1 bg-gray-100 rounded-full">{issue.category}</span>
                    <span>{issue.state}</span>
                    <span>{formatDate(issue.timestamp)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No issues reported yet</p>
              </div>
            )}
          </TabsContent>

          {/* Debates Tab */}
          <TabsContent value="debates" className="space-y-4">
            {debates.stats && debates.stats.total_debates > 0 && (
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow-sm p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900">{debates.stats.total_debates}</p><p className="text-sm text-gray-500">Total</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{debates.stats.wins}</p><p className="text-sm text-gray-500">Wins</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{debates.stats.losses}</p><p className="text-sm text-gray-500">Losses</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 text-center">
                  <p className="text-2xl font-bold text-gray-600">{debates.stats.draws}</p><p className="text-sm text-gray-500">Draws</p>
                </div>
              </div>
            )}

            {debates.rooms && debates.rooms.length > 0 ? (
              debates.rooms.map((room) => {
                const isParticipantA = room.participant_a_id === id;
                const userWon = (room.winner === 'A' && isParticipantA) || (room.winner === 'B' && !isParticipantA);
                const userLost = room.winner !== 'draw' && !userWon && room.status === 'ended';
                return (
                  <div key={room.room_id} onClick={() => room.status === 'ended' ? navigate(`/rooms/${room.room_id}/result`) : navigate(`/rooms/${room.room_id}`)} className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-testid={`debate-${room.room_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 line-clamp-1">{room.topic}</h3>
                      {room.status === 'ended' && (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${userWon ? 'bg-green-100 text-green-700' : userLost ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                          {userWon && <Trophy className="inline h-3 w-3 mr-1" />}
                          {userWon ? 'WON' : userLost ? 'LOST' : 'DRAW'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>{room.participant_a_name?.split(' ')[0]}</span>
                      <span className="text-gray-400">vs</span>
                      <span>{room.participant_b_name?.split(' ')[0] || 'Waiting...'}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">{formatDate(room.created_at)}</p>
                  </div>
                );
              })
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <Swords className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No debates yet</p>
              </div>
            )}
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-4">
            {loadingActivities ? (
              <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
            ) : activities.length > 0 ? (
              <div className="bg-white rounded-lg shadow-sm divide-y">
                {activities.map((activity, idx) => {
                  const Icon = getActivityIcon(activity.activity_type);
                  return (
                    <div key={idx} className="p-4 flex items-start gap-3" data-testid={`activity-${idx}`}>
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-800">{getActivityDescription(activity)}</p>
                        <p className="text-xs text-gray-500 mt-1">{formatDate(activity.created_at || activity.timestamp)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <Activity className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No recent activity</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between bg-gray-50">
              <h3 className="text-lg font-semibold">Edit Profile</h3>
              <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-gray-200 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh] space-y-6">
              {/* Basic Info Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Basic Info
                </h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Bio</label>
                      <PrivacySelect value={editForm.bio_privacy} onChange={(v) => setEditForm(p => ({ ...p, bio_privacy: v }))} />
                    </div>
                    <Textarea value={editForm.bio} onChange={(e) => setEditForm(p => ({ ...p, bio: e.target.value }))} placeholder="Tell people about yourself..." rows={3} maxLength={300} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Education</label>
                      <PrivacySelect value={editForm.education_privacy} onChange={(v) => setEditForm(p => ({ ...p, education_privacy: v }))} />
                    </div>
                    <Input value={editForm.education} onChange={(e) => setEditForm(p => ({ ...p, education: e.target.value }))} placeholder="e.g., B.Tech from IIT Delhi" maxLength={100} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Profession</label>
                      <PrivacySelect value={editForm.profession_privacy} onChange={(v) => setEditForm(p => ({ ...p, profession_privacy: v }))} />
                    </div>
                    <Input value={editForm.profession} onChange={(e) => setEditForm(p => ({ ...p, profession: e.target.value }))} placeholder="e.g., Software Engineer at Google" maxLength={100} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Location</label>
                      <PrivacySelect value={editForm.location_privacy} onChange={(v) => setEditForm(p => ({ ...p, location_privacy: v }))} />
                    </div>
                    <Input value={editForm.location} onChange={(e) => setEditForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g., Mumbai, India" maxLength={100} />
                  </div>
                </div>
              </div>
              
              {/* Personal Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3 flex items-center gap-2">
                  <Heart className="h-4 w-4" /> Personal
                </h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Relationship Status</label>
                      <PrivacySelect value={editForm.relationship_status_privacy} onChange={(v) => setEditForm(p => ({ ...p, relationship_status_privacy: v }))} />
                    </div>
                    <Select value={editForm.relationship_status || '__not_specified__'} onValueChange={(v) => setEditForm(p => ({ ...p, relationship_status: v === '__not_specified__' ? '' : v }))}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__not_specified__">Not specified</SelectItem>
                        {RELATIONSHIP_OPTIONS.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Political View</label>
                      <PrivacySelect value={editForm.political_view_privacy} onChange={(v) => setEditForm(p => ({ ...p, political_view_privacy: v }))} />
                    </div>
                    <Select value={editForm.political_view || '__not_specified__'} onValueChange={(v) => setEditForm(p => ({ ...p, political_view: v === '__not_specified__' ? '' : v }))}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__not_specified__">Not specified</SelectItem>
                        {POLITICAL_OPTIONS.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Interests</label>
                      <PrivacySelect value={editForm.interests_privacy} onChange={(v) => setEditForm(p => ({ ...p, interests_privacy: v }))} />
                    </div>
                    <Input value={editForm.interests} onChange={(e) => setEditForm(p => ({ ...p, interests: e.target.value }))} placeholder="e.g., Politics, Technology, Sports" maxLength={200} />
                  </div>
                </div>
              </div>
              
              {/* Favorites Section */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3 flex items-center gap-2">
                  <Star className="h-4 w-4" /> Favorites
                </h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Favorite Books</label>
                      <PrivacySelect value={editForm.favorite_books_privacy} onChange={(v) => setEditForm(p => ({ ...p, favorite_books_privacy: v }))} />
                    </div>
                    <Input value={editForm.favorite_books} onChange={(e) => setEditForm(p => ({ ...p, favorite_books: e.target.value }))} placeholder="e.g., 1984, The Alchemist" maxLength={200} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Favorite Movies</label>
                      <PrivacySelect value={editForm.favorite_movies_privacy} onChange={(v) => setEditForm(p => ({ ...p, favorite_movies_privacy: v }))} />
                    </div>
                    <Input value={editForm.favorite_movies} onChange={(e) => setEditForm(p => ({ ...p, favorite_movies: e.target.value }))} placeholder="e.g., Inception, 3 Idiots" maxLength={200} />
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button onClick={handleSaveProfile} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Badge Selection Modal */}
      {showBadgeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Choose Your Badge</h3>
              <button onClick={() => setShowBadgeModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {['national', 'political', 'civic', 'nature'].map(category => (
                <div key={category} className="mb-4">
                  <h4 className="text-sm font-medium text-gray-500 uppercase mb-2">{category}</h4>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.entries(badges)
                      .filter(([_, badge]) => badge.category === category)
                      .map(([key, badge]) => (
                        <button key={key} onClick={() => handleUpdateBadge(key)} className={`p-3 rounded-lg border-2 transition-all hover:scale-105 ${selectedBadge === key ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-testid={`badge-${key}`}>
                          <div className="w-10 h-10 mx-auto mb-1">
                            <img 
                              src={BADGE_ICONS[key] || DEFAULT_BADGE} 
                              alt={badge.name}
                              className="w-full h-full object-contain"
                              onError={(e) => { e.target.src = DEFAULT_BADGE; }}
                            />
                          </div>
                          <span className="text-xs text-gray-600 block text-center truncate">{badge.name}</span>
                        </button>
                      ))}
                  </div>
                </div>
              ))}
              <Button variant="outline" className="w-full mt-2" onClick={() => handleUpdateBadge('')}>Remove Badge</Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Friend Requests Modal */}
      {showFriendRequests && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Friend Requests</h3>
              <button onClick={() => setShowFriendRequests(false)} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {friendRequests.received?.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-500">Received</h4>
                  {friendRequests.received.map(request => (
                    <div key={request.request_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`friend-request-${request.request_id}`}>
                      <img src={getImageUrl(request.sender_picture) || '/default-avatar.png'} alt={request.sender_name} className="w-12 h-12 rounded-full object-cover" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{request.sender_name}</p>
                        <p className="text-xs text-gray-500">{formatDate(request.created_at)}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleRespondToFriendRequest(request.request_id, 'accept')} className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200" data-testid={`accept-${request.request_id}`}>
                          <CheckCircle className="h-5 w-5" />
                        </button>
                        <button onClick={() => handleRespondToFriendRequest(request.request_id, 'decline')} className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200" data-testid={`decline-${request.request_id}`}>
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No pending friend requests</p>
              )}
              
              {friendRequests.sent?.length > 0 && (
                <div className="space-y-3 mt-6">
                  <h4 className="text-sm font-medium text-gray-500">Sent</h4>
                  {friendRequests.sent.map(request => (
                    <div key={request.request_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center"><Clock className="h-5 w-5 text-gray-500" /></div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Pending...</p>
                        <p className="text-xs text-gray-500">{formatDate(request.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Friends List Modal */}
      {showFriendsList && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Friends ({friends.length})</h3>
              <button onClick={() => setShowFriendsList(false)} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {friends.length > 0 ? (
                <div className="space-y-3">
                  {friends.map(friend => (
                    <div key={friend.user_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100" onClick={() => { setShowFriendsList(false); navigate(`/profile/${friend.user_id}`); }} data-testid={`friend-${friend.user_id}`}>
                      <img src={getImageUrl(friend.picture) || '/default-avatar.png'} alt={friend.name} className="w-12 h-12 rounded-full object-cover" />
                      <div className="flex-1"><p className="font-medium text-gray-900">{friend.name}</p></div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No friends yet</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <footer className="mt-12 border-t border-gray-200 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-gray-500">
            <button 
              onClick={() => navigate('/about')} 
              className="hover:text-blue-600 transition-colors"
              data-testid="footer-about"
            >
              About
            </button>
            <button 
              onClick={() => navigate('/contact')} 
              className="hover:text-blue-600 transition-colors"
              data-testid="footer-contact"
            >
              Contact
            </button>
            <button 
              onClick={() => navigate('/privacy')} 
              className="hover:text-blue-600 transition-colors"
              data-testid="footer-privacy"
            >
              Privacy
            </button>
            <button 
              onClick={() => navigate('/terms')} 
              className="hover:text-blue-600 transition-colors"
              data-testid="footer-terms"
            >
              Terms
            </button>
            <button 
              onClick={() => navigate('/guidelines')} 
              className="hover:text-blue-600 transition-colors"
              data-testid="footer-guidelines"
            >
              Guidelines
            </button>
          </div>
          <p className="text-center text-gray-400 text-xs mt-4">
            &copy; {new Date().getFullYear()} POLIVOX. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Profile;
